import { defineStore } from "pinia";
import { getHttpClient } from "@/api/auth";
import { useToast } from "vue-toastification";

export const useFactureClientStore = defineStore("facturesClients", {
  state: () => ({
    factures: [],
    totalItems: null,
    totalPages: null,
    currentPage: 1,
    itemsPerPage: 10,
    sortBy: "Par_PiDt_Desc",
    loading: false,
    error: null,
    hasMorePages: true,
  }),

  getters: {
    getFacturesClients: state => state.factures ?? [],
    getTotalPages: state => state.totalPages || 1,
    getCurrentPage: state => state.currentPage,
    getIsLoading: state => state.loading,
    getError: state => state.error,
    getHasMorePages: state => state.hasMorePages,
  },

  actions: {
    async fetchFacturesClients(page = 1, sortBy = "Par_PiDt_Desc") {
      this.loading = true;
      this.error = null;
      this.currentPage = page;
      this.sortBy = sortBy;

      try {
        const response = await getHttpClient().get(
          `/divalto/factures/mes-factures-clients?page=${page}&itemsPerPage=${this.itemsPerPage}&orderby=${sortBy}`
        );
        const data = response.data;
        this.factures = Array.isArray(data) ? data : data.factures || [];

        this.factures = this.factures.map(item => ({
          PiDt: item.PIDT,
          HtMt: item.HTMT,
          PINO: item.PINO,
          PREFPINO: item.PREFPINO,
          TTCMT: item.TTCMT,
          PIREF: item.PIREF,
        }));

        this.hasMorePages = this.factures.length === this.itemsPerPage;

        this.totalItems = null;
        this.totalPages = this.hasMorePages ? page : page;
      } catch (error) {
        this.error = "Erreur lors du chargement des factures";
        this.factures = [];
        this.hasMorePages = false;
        this.totalItems = null;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },

    async downloadFactureClients(pino, prefPino, piref) {
      const toast = useToast();
      this.error = null;
      try {
        const response = await getHttpClient().get(
          `/divalto/factures/content/${encodeURIComponent(pino)}?PREFPINO=${encodeURIComponent(prefPino)}`,
          {
            responseType: "json",
          }
        );

        const base64Content = response.data?.content;
        if (!base64Content || base64Content.trim() === "") {
          this.error = "Ce fichier ne contient pas de contenu.";
          toast.error(this.error);
          return;
        }

        if (typeof base64Content !== "string" || !base64Content.startsWith("JVBERi")) {
          this.error = "Le contenu n'est pas une chaîne base64 valide pour un PDF.";
          toast.error(this.error);
          return;
        }

        let binaryString;
        try {
          binaryString = atob(base64Content);
        } catch (decodeError) {
          this.error = "Le contenu du fichier est invalide ou corrompu.";
          toast.error(this.error);
          return;
        }

        const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/pdf" });
        const safePiref = piref.replace(/[^a-zA-Z0-9_-]/g, "_");
        const filename = `Facture_${safePiref}.pdf`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Facture téléchargée avec succès !");
      } catch (error) {
        let errorMessage = "Erreur lors du téléchargement de la facture";
        if (error.response) {
          errorMessage = `Erreur serveur : ${error.response.status} - ${error.response.statusText}`;
        } else if (error.request) {
          errorMessage = "Aucune réponse du serveur.s";
        } else {
          errorMessage = error.message || errorMessage;
        }
        this.error = errorMessage;
        toast.error(errorMessage);
      }
    },

    changePage(page) {
      if (page >= 1 && (this.hasMorePages || page <= this.currentPage)) {
        this.fetchFacturesClients(page, this.sortBy);
      }
    },

    changeSort(sortBy) {
      this.sortBy = sortBy;
      this.currentPage = 1;
      this.hasMorePages = true;
      this.fetchFacturesClients(this.currentPage, this.sortBy);
    },
  },
});
